// AI-CONTEXT: See docs/patterns.md#data-taxonomy
// AI-CONTEXT: See docs/features/external-context-layer.md — this taxonomy drives
// external-context grain selection: a benchmark's primaryTargetGroups scope maps
// (via allowedPrimaryGroupsByStakeholderType → stakeholderType) to a context grain
// (household→HH, individual→DN, community→AC, organization/government→TG). Changing
// primaryTargetGroups or allowedPrimaryGroupsByStakeholderType shifts which external
// data a benchmark shows — keep the grain map in benchmarksEndpoint.js in sync.

// ─── Enums ───────────────────────────────────────────────────────────────────

export const stakeholderTypes = [
  'individual',
  'household',
  'community',
  'organization',
  'government',
  'other',
]

export const primaryTargetGroups = [
  'children',
  'youth',
  'adults',
  'older_people',
  'people_with_disabilities',
  'people_with_psychological_vulnerability',
  'people_with_distance_to_labour_market',
  'people_in_poverty',
  'people_with_debt',
  'people_with_migration_background',
  'refugees_status_holders_asylum_seekers',
  'homeless_or_houseless_people',
  'caregivers',
  'families_households',
  'neighbourhood_residents',
  'organizations',
  'governments',
  'other',
]

// Laag 3 = circumstance/situational vulnerability that can co-occur with ANY
// primary group. Pure restatements of a laag-2 identity were removed (ADR-009):
// homeless/houseless, migration_history/refugee_background, youth_at_risk, and
// the disability-type refinements (those become sub-attributes of the disability
// identity, not free-floating circumstances).
export const contextLabels = [
  'low_income',
  'unemployed',
  'long_term_unemployed',
  'neet',
  'single_parent',
  'socially_isolated',
  'lonely',
  'chronic_illness',
  'mental_health_issues',
  'addiction',
  'stress_burnout',
  'language_barrier',
  'low_literacy',
  'digital_exclusion',
  'newcomer',
  'undocumented',
  'housing_insecurity',
  'ex_offender',
  'victim_of_domestic_violence',
  'other',
]

// ─── Definitions ──────────────────────────────────────────────────────────────
// One sentence per label so the term means the same thing across organizations
// (ADR-009 principle 4). Age bands are the decided sector-variant boundaries.

export const stakeholderTypeDefinitions = {
  individual: 'Eén persoon als doelgroep.',
  household: 'Een huishouden of gezin als eenheid.',
  community: 'Een gemeenschap, buurt of groep met een gedeelde context.',
  organization: 'Een organisatie of instelling als doelgroep.',
  government: 'Een overheidsinstantie (gemeente, provincie, rijk).',
  other: 'Een ander type stakeholder dat niet in de overige typen past.',
}

export const primaryTargetGroupDefinitions = {
  children: 'Kinderen van 0 tot en met 11 jaar (basisschoolleeftijd).',
  youth: 'Jongeren van 12 tot en met 26 jaar.',
  adults: 'Volwassenen van 27 tot en met 66 jaar.',
  older_people: 'Ouderen van 67 jaar en ouder (AOW-leeftijd).',
  people_with_disabilities: 'Mensen met een lichamelijke, verstandelijke of zintuiglijke beperking.',
  people_with_psychological_vulnerability: 'Mensen met psychische kwetsbaarheid of een psychiatrische aandoening.',
  people_with_distance_to_labour_market: 'Mensen die zonder ondersteuning niet of moeilijk aan betaald werk komen.',
  people_in_poverty: 'Mensen die structureel onder de armoedegrens leven.',
  people_with_debt: 'Mensen met problematische schulden.',
  people_with_migration_background: 'Mensen die zelf, of van wie minstens één ouder, in het buitenland geboren is.',
  refugees_status_holders_asylum_seekers: 'Mensen die hun land zijn ontvlucht — asielzoekers, statushouders en vluchtelingen.',
  homeless_or_houseless_people: 'Mensen zonder eigen woonruimte (dak- of thuisloos).',
  caregivers: 'Mantelzorgers die onbetaald en langdurig voor een naaste zorgen.',
  families_households: 'Gezinnen of huishoudens als geheel.',
  neighbourhood_residents: 'Bewoners van een specifieke buurt of wijk.',
  organizations: 'Organisaties als doelgroep (bijv. andere maatschappelijke organisaties).',
  governments: 'Overheden als doelgroep (gemeente, provincie, rijk).',
  other: 'Een doelgroep die niet onder de overige categorieën valt.',
}

export const contextLabelDefinitions = {
  low_income: 'Een inkomen onder of rond het sociaal minimum.',
  unemployed: 'Zonder betaald werk en beschikbaar voor de arbeidsmarkt.',
  long_term_unemployed: 'Langer dan een jaar zonder betaald werk.',
  neet: 'Jongere die geen onderwijs volgt en geen werk heeft (Not in Education, Employment or Training).',
  single_parent: 'Alleenstaande ouder die zelfstandig één of meer kinderen opvoedt.',
  socially_isolated: 'Weinig of geen betekenisvolle sociale contacten.',
  lonely: 'Ervaart een gemis aan verbondenheid of gezelschap.',
  chronic_illness: 'Een langdurige of blijvende lichamelijke aandoening.',
  mental_health_issues: 'Psychische klachten of een psychiatrische aandoening.',
  addiction: 'Afhankelijkheid van middelen of gedrag (bijv. alcohol, drugs, gokken).',
  stress_burnout: 'Langdurige overbelasting of burn-outklachten.',
  language_barrier: 'Beheerst de voertaal onvoldoende om volwaardig mee te doen.',
  low_literacy: 'Moeite met lezen, schrijven of rekenen op basisniveau.',
  digital_exclusion: 'Onvoldoende toegang tot of vaardigheid met digitale middelen.',
  newcomer: 'Recent in Nederland aangekomen en nog bezig met inburgering.',
  undocumented: 'Verblijft zonder geldige verblijfsstatus.',
  housing_insecurity: 'Onzekere of instabiele woonsituatie, met risico op dakloosheid.',
  ex_offender: 'Heeft een detentieverleden en keert terug in de samenleving.',
  victim_of_domestic_violence: 'Slachtoffer van geweld of mishandeling binnen huiselijke kring.',
  other: 'Een omstandigheid die niet onder de overige kenmerken valt.',
}

// ─── Dutch labels ─────────────────────────────────────────────────────────────

export const stakeholderTypeLabels = {
  individual: 'Individu',
  household: 'Huishouden',
  community: 'Gemeenschap',
  organization: 'Organisatie',
  government: 'Overheid',
  other: 'Overig',
}

export const primaryTargetGroupLabels = {
  children: 'Kinderen',
  youth: 'Jongeren',
  adults: 'Volwassenen',
  older_people: 'Ouderen',
  people_with_disabilities: 'Mensen met een beperking',
  people_with_psychological_vulnerability: 'Mensen met psychische kwetsbaarheid',
  people_with_distance_to_labour_market: 'Mensen met afstand tot arbeidsmarkt',
  people_in_poverty: 'Mensen in armoede',
  people_with_debt: 'Mensen met schulden',
  people_with_migration_background: 'Mensen met migratieachtergrond',
  refugees_status_holders_asylum_seekers: 'Vluchtelingen / statushouders / asielzoekers',
  homeless_or_houseless_people: 'Daklozen / thuislozen',
  caregivers: 'Mantelzorgers',
  families_households: 'Gezinnen / huishoudens',
  neighbourhood_residents: 'Buurtbewoners',
  organizations: 'Organisaties',
  governments: 'Overheden',
  other: 'Overig',
}

export const contextLabelLabels = {
  low_income: 'Laag inkomen',
  unemployed: 'Werkloos',
  long_term_unemployed: 'Langdurig werkloos',
  neet: 'NEET (geen school/werk)',
  single_parent: 'Alleenstaande ouder',
  socially_isolated: 'Sociaal geïsoleerd',
  lonely: 'Eenzaam',
  physical_disability: 'Lichamelijke beperking',
  intellectual_disability: 'Verstandelijke beperking',
  mild_intellectual_disability: 'Lichte verstandelijke beperking',
  chronic_illness: 'Chronische ziekte',
  mental_health_issues: 'Psychische problematiek',
  addiction: 'Verslaving',
  stress_burnout: 'Stress / burn-out',
  language_barrier: 'Taalbarrière',
  low_literacy: 'Laaggeletterdheid',
  digital_exclusion: 'Digitale uitsluiting',
  migration_history: 'Migratiegeschiedenis',
  newcomer: 'Nieuwkomer',
  refugee_background: 'Vluchtelingenachtergrond',
  undocumented: 'Ongedocumenteerd',
  homeless: 'Dakloos',
  houseless: 'Thuisloos',
  housing_insecurity: 'Onzekere woonsituatie',
  youth_at_risk: 'Jongere in kwetsbare situatie',
  ex_offender: 'Ex-gedetineerde',
  victim_of_domestic_violence: 'Slachtoffer huiselijk geweld',
  'LGBTQIA+': 'LGBTQIA+',
  other: 'Overig',
}

// ─── Allowed primaryTargetGroups per stakeholderType ─────────────────────────

export const allowedPrimaryGroupsByStakeholderType = {
  individual: [
    'children',
    'youth',
    'adults',
    'older_people',
    'people_with_disabilities',
    'people_with_psychological_vulnerability',
    'people_with_distance_to_labour_market',
    'people_in_poverty',
    'people_with_debt',
    'people_with_migration_background',
    'refugees_status_holders_asylum_seekers',
    'homeless_or_houseless_people',
    'caregivers',
    'other'
  ],
  household: ['families_households'],
  community: ['neighbourhood_residents'],
  organization: ['organizations'],
  government: ['governments'],
  other: ['other'],
}

// ─── IRIS+ / SDG mapping ──────────────────────────────────────────────────────

// IRIS+ has no 1-to-1 metric per population. Where a demographic submetric of
// PI4060 (Client Individuals: Total) exists it is used; otherwise PI4060 itself,
// with the demographic carried by targetPopulations / a Target Stakeholder
// descriptor (PD5752 / PD2541). Codes verified against iris.thegiin.org.
// Generic / non-beneficiary groups (adults, families_households, organizations,
// governments, other) are intentionally left unmapped. See ADR-009.
// SYNC: functions/data/targetgroupCategories.js carries a CommonJS copy of this
// config (Decca's updateTocFields derives the same fields server-side). Change
// the mapping → change BOTH files; functions/data/targetgroupTaxonomy.test.js
// pins the parity.
export const impactMappingConfig = {
  // ── Groups with a verified IRIS+ demographic submetric ──
  children: {
    sdgs: ['SDG4', 'SDG3'],
    irisMetrics: ['PI1945'], // Client Individuals: Children/Adolescents
    targetPopulations: ['children'],
  },
  youth: {
    sdgs: ['SDG4', 'SDG8'],
    irisMetrics: ['PI1945'], // Client Individuals: Children/Adolescents
    targetPopulations: ['youth'],
  },
  people_in_poverty: {
    sdgs: ['SDG1'],
    irisMetrics: ['PI7098'], // Client Individuals: Low Income
    targetPopulations: ['low_income'],
  },
  people_with_debt: {
    sdgs: ['SDG1', 'SDG8'],
    irisMetrics: ['PI7098'], // Client Individuals: Low Income
    targetPopulations: ['low_income'],
  },
  people_with_disabilities: {
    sdgs: ['SDG10'],
    irisMetrics: ['PI6266'], // Client Individuals: People with Disabilities
    targetPopulations: ['disabled'],
  },
  people_with_migration_background: {
    sdgs: ['SDG10'],
    irisMetrics: ['PI4237'], // Client Individuals: Historically Marginalized
    targetPopulations: ['underserved'],
  },
  refugees_status_holders_asylum_seekers: {
    sdgs: ['SDG10', 'SDG16'],
    irisMetrics: ['PI5297'], // Client Individuals: Forcibly Displaced
    targetPopulations: ['refugees'],
  },
  homeless_or_houseless_people: {
    sdgs: ['SDG11', 'SDG1'],
    irisMetrics: ['PI4237'], // Client Individuals: Historically Marginalized
    targetPopulations: ['underserved'],
  },
  // ── Groups without a submetric → PI4060 + descriptor ──
  older_people: {
    sdgs: ['SDG3', 'SDG10'],
    irisMetrics: ['PI4060'], // Client Individuals: Total (demographic via descriptor)
    targetPopulations: ['aging_elderly'],
  },
  people_with_psychological_vulnerability: {
    sdgs: ['SDG3'],
    irisMetrics: ['PI4060'],
    targetPopulations: ['underserved'],
  },
  people_with_distance_to_labour_market: {
    sdgs: ['SDG8'],
    irisMetrics: ['PI4060'],
    targetPopulations: ['unemployed'],
  },
  caregivers: {
    sdgs: ['SDG3'],
    irisMetrics: ['PI4060'],
    targetPopulations: ['caregivers'],
  },
  neighbourhood_residents: {
    sdgs: ['SDG11'],
    irisMetrics: ['PI4060'],
    targetPopulations: ['community'],
  },
}

// ─── Validation ───────────────────────────────────────────────────────────────

export const validateTargetGroup = (data) => {
  if (!data.stakeholderType) return false
  // Exactly one primary target group drives the benchmark (ADR-009).
  if (!data.primaryTargetGroup) return false
  if (data.contextLabels && data.contextLabels.length > 5) return false
  return true
}

// ─── Normalisatie ─────────────────────────────────────────────────────────────

/**
 * Bring both taxonomy shapes onto one line: the UI (NodeEditor,
 * StakeholderManager-wizard) writes `primaryTargetGroups` (array, max 3);
 * ADR-009 consumers (benchmark, portfolio-sync) read `primaryTargetGroup`
 * (single). The first selected group is the primary one.
 *
 * @param {{stakeholderType?: string, primaryTargetGroup?: string, primaryTargetGroups?: string[], contextLabels?: string[]}} input
 * @returns {object} input with BOTH shapes filled and contextLabels always an array
 */
export const normalizeTargetGroupTaxonomy = (input = {}) => {
  const plural = Array.isArray(input.primaryTargetGroups)
    ? input.primaryTargetGroups.filter(Boolean)
    : []
  const groups = plural.length > 0
    ? plural
    : (input.primaryTargetGroup ? [input.primaryTargetGroup] : [])

  return {
    ...input,
    primaryTargetGroups: groups,
    primaryTargetGroup: groups[0] || '',
    contextLabels: Array.isArray(input.contextLabels) ? input.contextLabels : [],
  }
}

// ─── Derived fields ───────────────────────────────────────────────────────────

export const generateAnalysisTags = (data) => {
  const { stakeholderType, primaryTargetGroups, contextLabels } = normalizeTargetGroupTaxonomy(data)
  const tags = new Set()

  if (stakeholderType) tags.add(stakeholderType)
  primaryTargetGroups.forEach(t => tags.add(t))
  contextLabels.forEach(t => tags.add(t))

  if (primaryTargetGroups.includes('people_with_distance_to_labour_market')) {
    tags.add('labour_market_distance')
  }
  if (contextLabels.includes('low_income')) {
    tags.add('poverty_related')
  }

  return Array.from(tags)
}

export const generateImpactMapping = (data) => {
  const { primaryTargetGroups } = normalizeTargetGroupTaxonomy(data)
  const sdgs = new Set()
  const irisMetrics = new Set()
  const targetPopulations = new Set()

  primaryTargetGroups.forEach(group => {
    const mapping = impactMappingConfig[group]
    if (!mapping) return
    mapping.sdgs.forEach(s => sdgs.add(s))
    mapping.irisMetrics.forEach(i => irisMetrics.add(i))
    mapping.targetPopulations.forEach(t => targetPopulations.add(t))
  })

  return {
    sdgs: Array.from(sdgs),
    irisMetrics: Array.from(irisMetrics),
    targetPopulations: Array.from(targetPopulations),
  }
}

// ─── Save pipeline ────────────────────────────────────────────────────────────

/**
 * Prepare a doelgroep-taxonomy write. Deliberately WITHOUT completeness
 * validation: the TOC-editor (NodeEditor) auto-saves per chip click, so
 * partial states (only a type chosen, groups still empty) are legal — a
 * throw here makes every chip click silently dead (bug #60). Completeness
 * gating is the caller's job (the wizard gates its steps; use
 * validateTargetGroup for an explicit strict check).
 * AI-CONTEXT: See docs/pitfalls.md#ui-strict-validator-kills-autosave
 * AI-CONTEXT: See docs/pitfalls.md#dev-helper-contract-change-strands-callers
 *
 * @param {{stakeholderType?: string, primaryTargetGroup?: string, primaryTargetGroups?: string[], contextLabels?: string[]}} input
 * @returns {object} normalized input + derived analysisTags/impactMapping
 */
export const processTargetGroup = (input) => {
  const data = normalizeTargetGroupTaxonomy(input)

  return {
    ...data,
    analysisTags: generateAnalysisTags(data),
    impactMapping: generateImpactMapping(data),
  }
}

export default primaryTargetGroups
